// One mouse wheel, one meaning, in every canvas.
//
// Wheel zoom was written per canvas and got two things wrong in each:
//
//   DIRECTION. Wheel up must zoom IN. The split-search canvas did the
//   opposite - measured, a wheel-up took its scale from 0.473 to 0.411,
//   exactly divided by its own step. A reader nudging the wheel to look
//   closer watched the drawing recede.
//
//   PASSIVE LISTENERS. React attaches wheel handlers in a way the
//   browser treats as passive, so the e.preventDefault() inside them
//   does nothing - Chrome says so out loud: "Unable to preventDefault
//   inside passive event listener". The page keeps its own scroll and
//   pinch behaviour while the canvas is trying to zoom, which is why
//   zooming a large sheet felt like it fought back.
//
// Both are fixed once, here, by attaching the listener natively with
// { passive: false } and deriving the factor from the delta so the sign
// cannot be got backwards:
//
//   exp(-delta * SPEED) - delta < 0 (wheel up) gives a factor above 1
//
// Wheel deltas are also not comparable between devices: a mouse reports
// pixels, some report lines, a few report pages. They are normalised to
// pixels before use, so a trackpad and a wheel mouse behave the same.
import { useEffect } from "react";

// Chosen so one notch of a typical mouse (deltaY 100) is about 1.16x -
// close to the 1.15 step the canvases used, but continuous, so a
// trackpad's many small deltas add up smoothly instead of stepping.
const SPEED = 0.0015;
const LINE_PX = 16;
const PAGE_PX = 100;

/** A wheel event's delta in pixels, whatever the device reports. */
export function wheelDeltaPx(e) {
  const unit = e.deltaMode === 1 ? LINE_PX : e.deltaMode === 2 ? PAGE_PX : 1;
  return (e.deltaY || 0) * unit;
}

/**
 * The zoom factor for one wheel event. Above 1 zooms in.
 *
 * Clamped per event so a violent trackpad flick cannot jump the view
 * across several octaves in one frame.
 */
export function wheelZoomFactor(e, speed = SPEED) {
  const dy = wheelDeltaPx(e);
  if (!dy) return 1;
  return Math.min(3, Math.max(1 / 3, Math.exp(-dy * speed)));
}

/**
 * Attach wheel-to-zoom to an element.
 *
 * `onZoom(factor, point, event)` - `point` is in CLIENT coordinates;
 * the caller converts to its own space, since only it knows its
 * transform. Returning nothing is fine.
 *
 * The listener is native and non-passive, which is the whole point: a
 * React onWheel prop cannot preventDefault.
 */
export function useWheelZoom(ref, onZoom, deps = []) {
  useEffect(() => {
    const el = ref && ref.current;
    if (!el || typeof onZoom !== "function") return undefined;
    const handler = (e) => {
      // Let the page keep the wheel when the reader means to scroll it:
      // ctrl+wheel is the browser's own page zoom, and leaving it alone
      // is less surprising than overriding it.
      if (e.ctrlKey) return;
      const factor = wheelZoomFactor(e);
      if (factor === 1) return;
      e.preventDefault();
      onZoom(factor, { x: e.clientX, y: e.clientY }, e);
    };
    el.addEventListener("wheel", handler, { passive: false });
    return () => el.removeEventListener("wheel", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

/**
 * Zoom a { k, x, y } view about a point, keeping what is under the
 * cursor under the cursor. The arithmetic every canvas was repeating.
 */
export function zoomAbout(view, factor, px, py, min = 0.05, max = 12) {
  const k = Math.max(min, Math.min(max, view.k * factor));
  const applied = k / view.k;   // clamping may have shortened the step
  return {
    k,
    x: px - (px - view.x) * applied,
    y: py - (py - view.y) * applied,
  };
}

export default useWheelZoom;
